import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Search,
  LogOut,
  ArrowRight,
  X,
  Clock,
  CircleDot,
  CheckCircle2,
  XCircle,
  CalendarRange,
  ShieldCheck,
} from "lucide-react";
import { BrandLockup } from "@/components/Logo";
import {
  DEMO_COMPANIES,
  DEMO_RESERVATIONS,
  DEMO_STATUSES,
  ACTIVE_STATUSES,
  UPCOMING_STATUSES,
} from "@/data/corporateDemo";

const TONE = {
  slate: "border-slate-400/30 bg-slate-400/10 text-slate-300",
  blue: "border-blue-400/30 bg-blue-400/10 text-blue-300",
  violet: "border-violet-400/30 bg-violet-400/10 text-violet-300",
  amber: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  gold: "border-gold/40 bg-gold/15 text-gold",
  green: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  red: "border-red-400/30 bg-red-400/10 text-red-300",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function StatusBadge({ status }) {
  const s = DEMO_STATUSES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${TONE[s.tone]}`}>
      <CircleDot className="h-3 w-3" /> {s.label}
    </span>
  );
}

function DemoBanner() {
  return (
    <div className="border-b border-gold/20 bg-gold/10 px-5 py-2 text-center" data-testid="corporate-demo-banner">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">
        Demonstration · Fictional data · Not connected to Limo Anywhere
      </p>
    </div>
  );
}

function Login({ onPick }) {
  return (
    <main className="relative flex min-h-screen flex-col bg-obsidian-900" data-testid="corporate-demo-login">
      <DemoBanner />
      <div className="flex flex-1 items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="gold-frame w-full max-w-lg rounded-3xl bg-obsidian-800 p-10 text-center sm:p-14"
        >
          <BrandLockup className="mx-auto h-20 w-auto" />
          <span className="mt-7 inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
            <Building2 className="h-3 w-3" /> Corporate Portal · Demo
          </span>
          <h1 className="mt-5 font-display text-4xl font-bold text-ivory">Sign in to your company</h1>
          <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-silver/80">
            Choose a sample corporate account to preview the dashboard. In
            production, access is secured per company and granted only after
            Nanak Chauffeurs approves your account.
          </p>
          <div className="mt-8 space-y-3">
            {DEMO_COMPANIES.map((c) => (
              <button
                key={c.id}
                onClick={() => onPick(c)}
                data-testid={`corporate-demo-company-${c.id}`}
                className="group flex w-full items-center justify-between rounded-xl border border-gold/15 bg-obsidian-900 px-5 py-4 text-left transition-colors hover:border-gold/45"
              >
                <span>
                  <span className="block font-display text-lg font-semibold text-ivory">{c.name}</span>
                  <span className="block font-mono text-[11px] text-ivory/45">{c.code}</span>
                </span>
                <ArrowRight className="h-5 w-5 text-gold transition-transform group-hover:translate-x-1" />
              </button>
            ))}
          </div>
          <Link to="/" data-testid="corporate-demo-home" className="mt-8 inline-block font-sans text-sm font-medium text-ivory/60 hover:text-gold">
            ← Return to Home
          </Link>
        </motion.div>
      </div>
    </main>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl border border-gold/15 bg-obsidian-800 p-5" data-testid={testId}>
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 text-gold">
        <Icon className="h-4 w-4" />
      </span>
      <p className="mt-4 font-display text-3xl font-semibold text-ivory">{value}</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ivory/45">{label}</p>
    </div>
  );
}

export default function CorporateDemo() {
  const [company, setCompany] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [when, setWhen] = useState("all");
  const [detail, setDetail] = useState(null);

  const all = useMemo(
    () => (company ? DEMO_RESERVATIONS.filter((r) => r.company === company.id) : []),
    [company]
  );

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      total: all.length,
      upcoming: all.filter((r) => UPCOMING_STATUSES.includes(r.status)).length,
      active: all.filter((r) => ACTIVE_STATUSES.includes(r.status)).length,
      completed: all.filter((r) => r.status === "completed").length,
      canceled: all.filter((r) => r.status === "canceled").length,
      _now: now,
    };
  }, [all]);

  const rows = useMemo(() => {
    const now = Date.now();
    return all
      .filter((r) => {
        if (statusFilter !== "all" && r.status !== statusFilter) return false;
        if (when === "upcoming" && new Date(r.pickupAt).getTime() < now) return false;
        if (when === "past" && new Date(r.pickupAt).getTime() >= now) return false;
        const q = query.trim().toLowerCase();
        if (q && !(`${r.passenger} ${r.id} ${r.pickup} ${r.dropoff}`.toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) => new Date(a.pickupAt) - new Date(b.pickupAt));
  }, [all, statusFilter, when, query]);

  if (!company) return <Login onPick={setCompany} />;

  return (
    <main className="min-h-screen bg-obsidian-900" data-testid="corporate-demo-dashboard">
      <DemoBanner />
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:px-12">
        {/* header */}
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Corporate Dashboard</p>
            <h1 className="mt-3 font-display text-4xl font-semibold text-ivory sm:text-5xl" data-testid="corporate-demo-company-name">
              {company.name}
            </h1>
            <p className="mt-2 font-mono text-[11px] text-ivory/45">
              Account {company.code} · Last sync {new Date().toLocaleTimeString()}
            </p>
          </div>
          <button
            onClick={() => { setCompany(null); setDetail(null); }}
            data-testid="corporate-demo-signout"
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 font-sans text-sm font-medium text-gold transition-colors hover:bg-gold hover:text-obsidian-900"
          >
            <LogOut className="h-4 w-4" /> Switch account
          </button>
        </div>

        {/* stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <Stat icon={CalendarRange} label="Total" value={stats.total} testId="corporate-stat-total" />
          <Stat icon={Clock} label="Upcoming" value={stats.upcoming} testId="corporate-stat-upcoming" />
          <Stat icon={CircleDot} label="Active" value={stats.active} testId="corporate-stat-active" />
          <Stat icon={CheckCircle2} label="Completed" value={stats.completed} testId="corporate-stat-completed" />
          <Stat icon={XCircle} label="Canceled" value={stats.canceled} testId="corporate-stat-canceled" />
        </div>

        {/* controls */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ivory/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search passenger, res #, location"
              data-testid="corporate-search"
              className="field pl-11"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="corporate-filter-status" className="field max-w-[220px]">
            <option value="all">All statuses</option>
            {Object.entries(DEMO_STATUSES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select value={when} onChange={(e) => setWhen(e.target.value)} data-testid="corporate-filter-when" className="field max-w-[180px]">
            <option value="all">All dates</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>
        </div>

        {/* table (desktop) */}
        <div className="mt-6 hidden overflow-hidden rounded-2xl border border-gold/15 lg:block" data-testid="corporate-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-obsidian-800 font-mono text-[10px] uppercase tracking-[0.14em] text-ivory/45">
              <tr>
                <th className="px-5 py-4">Res #</th>
                <th className="px-5 py-4">Passenger</th>
                <th className="px-5 py-4">Pickup</th>
                <th className="px-5 py-4">Route</th>
                <th className="px-5 py-4">Driver</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setDetail(r)}
                  data-testid={`corporate-row-${r.id}`}
                  className="cursor-pointer border-t border-gold/10 transition-colors hover:bg-obsidian-800"
                >
                  <td className="px-5 py-4 font-mono text-gold">{r.id}</td>
                  <td className="px-5 py-4 text-ivory">{r.passenger}</td>
                  <td className="px-5 py-4 text-ivory/70">{fmtDate(r.pickupAt)}</td>
                  <td className="px-5 py-4 text-ivory/60">{r.pickup} → {r.dropoff}</td>
                  <td className="px-5 py-4 text-ivory/70">{r.driver}</td>
                  <td className="px-5 py-4"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-ivory/45">No reservations match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* cards (mobile) */}
        <div className="mt-6 space-y-3 lg:hidden">
          {rows.map((r) => (
            <button
              key={r.id}
              onClick={() => setDetail(r)}
              data-testid={`corporate-card-${r.id}`}
              className="block w-full rounded-2xl border border-gold/15 bg-obsidian-800 p-5 text-left"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-gold">{r.id}</span>
                <StatusBadge status={r.status} />
              </div>
              <p className="mt-3 font-display text-lg font-semibold text-ivory">{r.passenger}</p>
              <p className="mt-1 text-xs text-ivory/55">{fmtDate(r.pickupAt)}</p>
              <p className="mt-2 text-sm text-ivory/70">{r.pickup} → {r.dropoff}</p>
            </button>
          ))}
          {rows.length === 0 && <p className="py-10 text-center text-ivory/45">No reservations match your filters.</p>}
        </div>

        <p className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-ivory/35">
          <ShieldCheck className="h-3.5 w-3.5 text-gold/60" />
          In production, company-level access is enforced on the server — you only ever see your own reservations.
        </p>
      </div>

      {/* detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-obsidian-900/70 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <motion.aside
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="h-full w-full max-w-md overflow-y-auto border-l border-gold/20 bg-obsidian-800 p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="corporate-detail"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-gold">{detail.id}</span>
              <button onClick={() => setDetail(null)} data-testid="corporate-detail-close" className="text-ivory/60 hover:text-gold"><X className="h-5 w-5" /></button>
            </div>
            <h2 className="mt-5 font-display text-3xl font-semibold text-ivory">{detail.passenger}</h2>
            <div className="mt-4"><StatusBadge status={detail.status} /></div>
            <dl className="mt-8 space-y-5 text-sm">
              {[
                ["Pickup time", fmtDate(detail.pickupAt)],
                ["Pickup", detail.pickup],
                ["Drop-off", detail.dropoff],
                ["Driver", detail.driver],
                ["Vehicle", detail.vehicle],
                ["Latest status", DEMO_STATUSES[detail.status].label],
                ["Last sync", new Date().toLocaleTimeString()],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-6 border-b border-gold/10 pb-4">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-ivory/45">{k}</dt>
                  <dd className="text-right text-ivory/85">{v}</dd>
                </div>
              ))}
            </dl>
          </motion.aside>
        </div>
      )}
    </main>
  );
}
